import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight, X, Calendar, Gauge } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import RequestForm from "@/components/RequestForm";
import api, { resolveImg } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const CATALOG_HERO = "https://images.unsplash.com/photo-1742702596008-5a02e0a8ad49?w=1920&q=80";
const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1764552283704-ed67e7ad6059?w=600";
const STATUS_MAP = {
    in_stock: { label: "В наличии", cls: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" },
    in_transit: { label: "В пути", cls: "bg-amber-500/20 text-amber-400 border border-amber-500/30" },
    sold: { label: "Продан", cls: "bg-zinc-500/20 text-zinc-400 border border-zinc-500/30" },
};

function CarDetail({ car, onBook }) {
    const [ci, setCi] = useState(0);
    const imgs = car.images?.length ? car.images.map(resolveImg) : [PLACEHOLDER_IMG];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid={`car-detail-${car.id}`}>
            <div>
                <div className="relative aspect-[4/3] overflow-hidden bg-black/50 mb-2 sm:mb-3">
                    <img src={imgs[ci]} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" />
                    {imgs.length > 1 && (
                        <>
                            <button onClick={() => setCi((ci - 1 + imgs.length) % imgs.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 p-1.5 sm:p-2 text-white hover:bg-black/80 transition-colors" data-testid="detail-prev-img"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                            <button onClick={() => setCi((ci + 1) % imgs.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 p-1.5 sm:p-2 text-white hover:bg-black/80 transition-colors" data-testid="detail-next-img"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                            <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {imgs.map((_, i) => (
                                    <button key={i} onClick={() => setCi(i)} className={`w-2 h-2 rounded-full transition-all ${ci === i ? "bg-[#0099FF] w-4 sm:w-5" : "bg-white/40 hover:bg-white/70"}`} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
                {imgs.length > 1 && (
                    <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
                        {imgs.map((img, i) => (
                            <button key={i} onClick={() => setCi(i)} className={`flex-shrink-0 w-12 h-9 sm:w-16 sm:h-12 overflow-hidden border-2 transition-all ${ci === i ? "border-[#0099FF]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex flex-col">
                <div className="mb-3 sm:mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white font-display">{car.brand} {car.model}</h2>
                    <div className="flex items-center gap-3 mt-2">
                        <span className={`inline-block text-xs px-3 py-1 ${STATUS_MAP[car.status]?.cls || "bg-zinc-500/20 text-zinc-400"}`}>{STATUS_MAP[car.status]?.label || car.status}</span>
                        <p className="text-[#E31E24] font-bold text-xl sm:text-2xl font-body">{car.price?.toLocaleString()} &#8381;</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="bg-black/40 border border-white/5 p-2.5 sm:p-3 text-center">
                        <Calendar className="w-4 h-4 mx-auto mb-1 text-[#0099FF]" />
                        <p className="text-zinc-500 text-xs font-body">Год</p>
                        <p className="text-white text-sm font-semibold font-body">{car.year}</p>
                    </div>
                    <div className="bg-black/40 border border-white/5 p-2.5 sm:p-3 text-center">
                        <Gauge className="w-4 h-4 mx-auto mb-1 text-[#0099FF]" />
                        <p className="text-zinc-500 text-xs font-body">Пробег</p>
                        <p className="text-white text-sm font-semibold font-body">{car.mileage?.toLocaleString()} км</p>
                    </div>
                </div>
                {car.description && (
                    <div className="mb-4 sm:mb-6 flex-1">
                        <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-2 font-body">Описание</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-wrap font-body" data-testid="car-detail-description">{car.description}</p>
                    </div>
                )}
                <button onClick={() => onBook(car)} className="w-full bg-[#0099FF] text-white py-3 text-sm uppercase tracking-wider font-bold hover:shadow-[0_0_20px_rgba(0,153,255,0.4)] transition-all" data-testid="detail-book-btn">
                    {car.status === "sold" ? "Найти аналог" : "Забронировать"}
                </button>
            </div>
        </div>
    );
}

export default function CatalogPage() {
    const [cars, setCars] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showBooking, setShowBooking] = useState(false);
    const [bookingCar, setBookingCar] = useState(null);
    const [selectedCar, setSelectedCar] = useState(null);
    const [imgIdx, setImgIdx] = useState({});
    const settings = useSettings();

    useEffect(() => {
        api.get("/cars").then((r) => {
            setCars(r.data);
            setBrands([...new Set(r.data.map((c) => c.brand))]);
        }).catch(() => {});
    }, []);

    const filtered = cars.filter((c) => {
        if (selectedBrand !== "all" && c.brand !== selectedBrand) return false;
        if (selectedStatus !== "all" && c.status !== selectedStatus) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!c.brand.toLowerCase().includes(q) && !c.model.toLowerCase().includes(q)) return false;
        }
        return true;
    });

    const handleBook = (car) => {
        setSelectedCar(null);
        setBookingCar(car);
        setShowBooking(true);
    };

    return (
        <>
        <title>Каталог | AutoTreider</title>
        <meta name="description" content={`${settings?.catalog_title || "Каталог автомобилей"} из Европы в наличии и под заказ.`} />
        <link rel="canonical" href="https://autotreider.eu/catalog" />
        <meta property="og:title" content={`${settings?.catalog_title || "Каталог"} | ${settings?.company_name || "AutoTreider"}`} />
        <meta property="og:description" content={`${settings?.catalog_title || "Каталог автомобилей"} из Европы.`} />
        <meta property="og:url" content="https://autotreider.eu/catalog" />
        <div className="min-h-screen pt-16 sm:pt-20" data-testid="catalog-page">
            <section className="relative h-[30vh] sm:h-[40vh] flex items-center">
                <div className="absolute inset-0">
                    <img src={CATALOG_HERO} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/70" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                    <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-2 sm:mb-3 font-body">{settings?.catalog_subtitle || "Каталог"}</p>
                    <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white font-display">{settings?.catalog_title || "Автомобили в наличии"}</h1>
                </div>
            </section>

            <section className="py-4 sm:py-8 border-b border-white/5" data-testid="catalog-filters">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
                    <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Поиск по марке или модели..." className="pl-10 bg-black/50 border-white/10 focus:border-[#0099FF] text-white h-11 sm:h-12 rounded-none text-sm" data-testid="catalog-search-input" />
                    </div>
                    <div className="flex gap-3">
                        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                            <SelectTrigger className="flex-1 sm:w-[180px] bg-black/50 border-white/10 text-white h-11 sm:h-12 rounded-none text-sm" data-testid="catalog-brand-filter"><SelectValue placeholder="Марка" /></SelectTrigger>
                            <SelectContent className="bg-[#18181b] border-white/10">
                                <SelectItem value="all">Все марки</SelectItem>
                                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="flex-1 sm:w-[180px] bg-black/50 border-white/10 text-white h-11 sm:h-12 rounded-none text-sm" data-testid="catalog-status-filter"><SelectValue placeholder="Статус" /></SelectTrigger>
                            <SelectContent className="bg-[#18181b] border-white/10">
                                <SelectItem value="all">Все статусы</SelectItem>
                                <SelectItem value="in_stock">В наличии</SelectItem>
                                <SelectItem value="in_transit">В пути</SelectItem>
                                <SelectItem value="sold">Продан</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </section>

            <section className="py-8 sm:py-12" data-testid="catalog-grid">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                    {filtered.length === 0 ? (
                        <div className="text-center py-20"><p className="text-zinc-500 font-body">Автомобили не найдены</p></div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map((car, i) => {
                                const ci = imgIdx[car.id] || 0;
                                return (
                                    <motion.div key={car.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="group bg-[#09090b] border border-white/5 hover:border-[#0099FF]/50 transition-all overflow-hidden cursor-pointer" onClick={() => setSelectedCar(car)} data-testid={`car-card-${car.id}`}>
                                        <div className="relative aspect-[16/10] overflow-hidden">
                                            <img src={resolveImg(car.images?.[ci]) || PLACEHOLDER_IMG} alt={`${car.brand} ${car.model}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                            {car.images?.length > 1 && (
                                                <>
                                                    <button onClick={(e) => { e.stopPropagation(); setImgIdx((p) => ({ ...p, [car.id]: (ci - 1 + car.images.length) % car.images.length })); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronLeft className="w-4 h-4" /></button>
                                                    <button onClick={(e) => { e.stopPropagation(); setImgIdx((p) => ({ ...p, [car.id]: (ci + 1) % car.images.length })); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            <div className="absolute top-3 left-3">
                                                <span className={`text-xs px-3 py-1 ${STATUS_MAP[car.status]?.cls || "bg-zinc-500/20 text-zinc-400"}`}>{STATUS_MAP[car.status]?.label || car.status}</span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="text-white font-semibold text-lg font-body">{car.brand} {car.model}</h3>
                                            <div className="flex gap-4 text-zinc-500 text-sm mb-4 font-body"><span>{car.year}</span><span>{car.mileage?.toLocaleString()} км</span></div>
                                            <div className="flex items-center justify-between">
                                                <p className="text-[#E31E24] font-bold text-xl font-body">{car.price?.toLocaleString()} &#8381;</p>
                                                <button onClick={(e) => { e.stopPropagation(); handleBook(car); }} className="bg-[#0099FF] text-white px-5 py-2 text-xs uppercase tracking-wider font-bold hover:shadow-[0_0_15px_rgba(0,153,255,0.5)] transition-all" data-testid={`book-car-${car.id}`}>
                                                    {car.status === "sold" ? "Аналог" : "Забронировать"}
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            <Dialog open={!!selectedCar} onOpenChange={(open) => { if (!open) setSelectedCar(null); }}>
                <DialogContent className="bg-[#09090b] border-white/10 w-[calc(100%-1rem)] sm:w-auto max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="car-detail-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white font-display text-lg sm:text-xl">{selectedCar ? `${selectedCar.brand} ${selectedCar.model}` : ""}</DialogTitle>
                    </DialogHeader>
                    {selectedCar && <CarDetail car={selectedCar} onBook={handleBook} />}
                </DialogContent>
            </Dialog>

            <Dialog open={showBooking} onOpenChange={setShowBooking}>
                <DialogContent className="bg-[#09090b] border-white/10 w-[calc(100%-1rem)] sm:w-auto max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white font-display text-lg">{bookingCar ? `${bookingCar.brand} ${bookingCar.model}` : "Забронировать"}</DialogTitle>
                    </DialogHeader>
                    <RequestForm source="catalog" carData={bookingCar || {}} onSuccess={() => setShowBooking(false)} />
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
